export type ExternalAccountProvider =
  | "google"
  | "microsoft"
  | "dav"
  | "caldav"
  | "carddav"
  | "ics"
  // booking software, connected through its configurator
  | "provalliance"
  | "calendly"
  | "website";

/** The providers whose accounts are booking software. */
export const BOOKING_PROVIDERS = [
  "provalliance",
  "calendly",
  "website",
] as const;
export type BookingProvider = (typeof BOOKING_PROVIDERS)[number];

export const isBookingProvider = (
  provider: string | undefined,
): provider is BookingProvider =>
  BOOKING_PROVIDERS.includes(provider as BookingProvider);

export type ExternalAccountCapability =
  | "calendar"
  | "email"
  | "contacts"
  | "notes"
  | "booking"
  | "video";

export type ExternalAccountAuthType = "oauth2" | "basic" | "none";

export type ExternalAccountStatus = "active" | "inactive" | "error";

export interface ExternalAccount {
  id: string;
  client_id: string;
  name: string;
  capabilities: { capabilities: ExternalAccountCapability[] };
  provider: ExternalAccountProvider;
  provider_identifier: string;
  auth_type: ExternalAccountAuthType;
  /** A booking site's URL; a CalDAV/ICS server. */
  server_url?: string;
  /** What a provider needs beside its URL and secret (never the secret). */
  provider_settings?: Record<string, string>;
  token_expires_at: number;
  scopes: { scopes: string[] };
  status: ExternalAccountStatus;
  last_error: string;
  last_sync_at: number;
  supports_calendar_write: boolean;
  supports_calendar_create: boolean;
  supports_contacts_write: boolean;
  supports_contacts_book_create: boolean;
  created_at: number;
  created_by: string;
  updated_at: number;
}

export interface CreateExternalAccountData {
  name: string;
  provider: ExternalAccountProvider;
  provider_identifier: string;
  capabilities: ExternalAccountCapability[];
  auth_type?: ExternalAccountAuthType;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: number;
  scopes?: string[];
}

export interface UpdateExternalAccountData {
  name?: string;
  capabilities?: { capabilities: ExternalAccountCapability[] };
  status?: ExternalAccountStatus;
}

export interface CalDAVSetupData {
  server_url: string;
  username: string;
  password: string;
  name?: string;
}

export interface VerifyAccessResult {
  valid: boolean;
  error?: string;
}

export interface RenewAccessResult {
  success: boolean;
  error?: string;
}

export interface ExternalAccountSettings {
  id: string;
  client_id: string;
  contacts_addressbook_id: string | null;
  contacts_addressbook_name: string | null;
  contacts_addressbook_account_id: string | null;
  primary_calendar_id: string | null;
  primary_calendar_name: string | null;
  primary_calendar_account_id: string | null;
  availability_calendar_ids: {
    ids: Array<{
      id: string;
      name: string;
    }>;
  };
}

export interface RemoteCalendar {
  id: string;
  name: string;
  read_only?: boolean;
}

export interface RemoteAddressbook {
  id: string;
  name: string;
  read_only?: boolean;
}
